import React from 'react'
import { DASHBOARD_CONTEXT } from '@trustero/trustero-api-web/lib/dashboard/dashboard_pb'
import { useDashboardFilterValues } from 'src/Utils/dashboards/dashboards.hooks'
import { NONE_ID } from 'src/Utils/globalConstants'
import { FilterName, FilterParam } from '../../../FilterBar.types'
import { FilterDropdown } from '../../FilterDropdown'

const normalizeDashboardDimensionValue = (rawValue: string): string[] => {
  const trimmedValue = rawValue.trim()
  if (!trimmedValue) {
    return []
  }
  if (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) {
    try {
      const parsedValue = JSON.parse(trimmedValue)
      if (Array.isArray(parsedValue)) {
        return parsedValue
          .map((item) => String(item).trim())
          .filter((item) => item.length > 0)
      }
    } catch {
      // fall through to raw string handling
    }
  }
  return [trimmedValue]
}

export const DashboardDimensionFilterDropdown = ({
  isNested = false,
  filterParam,
  filterName,
  dashboardContext,
  dashboardName,
  dimension,
  undefinedString,
  defaultSelection,
}: {
  isNested?: boolean
  filterParam: FilterParam
  filterName: FilterName
  dashboardContext: DASHBOARD_CONTEXT
  dashboardName: string
  dimension: string
  undefinedString?: string
  // What counts as selected when the URL has no value; the dashboard must apply the same.
  defaultSelection?: string[]
}): JSX.Element => {
  const { data } = useDashboardFilterValues(
    dashboardContext,
    dashboardName,
    dimension,
  )
  // NONE_ID, not '': the backend maps the literal "none" to "this dimension is absent"
  // (converters.go), which is what points with no such dimension need. An empty value takes the
  // ordinary branch instead and matches only a dimension that is present and blank — so the
  // option rendered but never filtered anything.
  const anonymousValue = undefinedString
    ? {
        label: undefinedString,
        value: NONE_ID,
      }
    : undefined
  const normalizedDimensionValues = Array.from(
    new Set(
      (data?.getFilterValuesList() || []).flatMap(
        normalizeDashboardDimensionValue,
      ),
    ),
  ).sort((a, b) => a.localeCompare(b))

  const filterValues = [
    ...(anonymousValue ? [anonymousValue] : []),
    ...normalizedDimensionValues.map((filterString) => ({
      label: filterString,
      value: filterString,
    })),
  ]

  return (
    <FilterDropdown
      filterParam={filterParam}
      filterName={filterName}
      filterValues={filterValues}
      isNested={isNested}
      defaultSelection={defaultSelection}
    />
  )
}
